/**
 * LinkPrefetch — hover-priority + idle-priority prefetcher.
 *
 * Two stages:
 *   1. When the page is idle, prefetch the four top-nav targets — /,
 *      /film/, /photography/, /about/ — so the most likely next page is
 *      already in cache before the user even hovers.
 *   2. When the user hovers (or touches) any internal link for more than
 *      ~100 ms, prefetch that URL too. By the time they actually click
 *      and the PageTransition overlay finishes fading in (~320 ms), the
 *      browser is loading from cache.
 *
 * Result: navigation between Home / Film / Photography / About / any
 * single portfolio feels instant rather than waiting on a TTFB round-trip.
 *
 * Footprint: ~1.5 kB minified. Uses native `<link rel="prefetch">`.
 * Skips: external links, mailto, tel, anchor-only, prefetched-already,
 * data-saver mode, slow 2G, low memory.
 */

const HOVER_DELAY_MS  = 100;
const ALREADY         = new Set();   // hrefs we've already issued
const MAX_PREFETCHES  = 16;

function shouldSkip() {
	const c = navigator.connection;
	if ( ! c ) return false;
	if ( c.saveData ) return true;
	if ( /2g/.test( c.effectiveType || '' ) ) return true;
	if ( navigator.deviceMemory && navigator.deviceMemory < 2 ) return true;
	return false;
}

function prefetch( href ) {
	if ( ALREADY.has( href ) ) return;
	if ( ALREADY.size >= MAX_PREFETCHES ) return;
	ALREADY.add( href );
	const link = document.createElement( 'link' );
	link.rel  = 'prefetch';
	link.as   = 'document';
	link.href = href;
	document.head.appendChild( link );
}

function isInternalNavigable( link ) {
	if ( ! link || link.target === '_blank' ) return false;
	const href = link.getAttribute( 'href' );
	if (
		! href ||
		href.startsWith( '#' ) ||
		href.startsWith( 'mailto:' ) ||
		href.startsWith( 'tel:' )
	) return false;
	try {
		const url = new URL( href, location.href );
		if ( url.origin !== location.origin ) return false;
		if ( url.pathname === location.pathname && url.search === location.search ) return false;
		// Skip wp-admin / file downloads.
		if ( url.pathname.startsWith( '/wp-admin' ) ) return false;
		if ( /\.(pdf|zip|jpg|jpeg|png|webp|avif|mp4|svg)$/i.test( url.pathname ) ) return false;
		return url.href;
	} catch {
		return false;
	}
}

export default class LinkPrefetch {
	constructor() {
		if ( shouldSkip() ) return;
		this._bindHovers();
		this._prefetchPrimaryNav();
	}

	// Stage 1 — fire-and-forget the four primary nav targets when idle.
	_prefetchPrimaryNav() {
		const fire = () => {
			document.querySelectorAll( '.main-nav__list a[href]' ).forEach( ( a ) => {
				const href = isInternalNavigable( a );
				if ( href ) prefetch( href );
			} );
		};
		if ( 'requestIdleCallback' in window ) {
			requestIdleCallback( fire, { timeout: 2500 } );
		} else {
			setTimeout( fire, 1500 );
		}
	}

	// Stage 2 — prefetch any hovered/touched link after a short delay
	// (avoids prefetching mouse-fly-by traffic).
	_bindHovers() {
		let pending;

		const cancel = () => {
			if ( pending ) {
				clearTimeout( pending );
				pending = null;
			}
		};

		document.addEventListener( 'pointerover', ( e ) => {
			const link = e.target.closest && e.target.closest( 'a[href]' );
			if ( ! link ) return;
			const href = isInternalNavigable( link );
			if ( ! href ) return;
			cancel();
			pending = setTimeout( () => prefetch( href ), HOVER_DELAY_MS );
		}, { passive: true } );

		document.addEventListener( 'pointerout',  cancel, { passive: true } );
		// Touch devices — fire on touchstart too (no hover state).
		document.addEventListener( 'touchstart', ( e ) => {
			const link = e.target.closest && e.target.closest( 'a[href]' );
			if ( ! link ) return;
			const href = isInternalNavigable( link );
			if ( href ) prefetch( href );
		}, { passive: true } );
	}
}
